/** 后台模块管理使用的分类目录。 */

export type ProfessionalModuleId = 'finance-management' | 'legal-compliance-assistant';

/** 模块两大类：客户业务能力 vs 平台运行时。 */
export type ModuleClassId = 'business' | 'platform';

/** 计费授权策略：与 moduleAccessPolicy 保持一致。 */
export type ModuleEntitlementPolicy = 'always_require' | 'require_when_listed';

/** 内核组件对任务入口的影响类型。 */
export type ModuleComponentKind = 'runtime' | 'task-gate';

export const PROFESSIONAL_MODULE_IDS: ProfessionalModuleId[] = [
  'finance-management',
  'legal-compliance-assistant',
];

export type ModuleClassDefinition = {
  id: ModuleClassId;
  name: string;
  summary: string;
};

export type ProfessionalModuleDefinition = {
  id: ProfessionalModuleId;
  classId: 'business';
  name: string;
  summary: string;
  capabilities: string[];
  entitlementPolicy: ModuleEntitlementPolicy;
  entitlementPolicyLabel: string;
  serviceLinks: Array<{label: string; href: string}>;
};

export type ModuleComponentDefinition = {
  id: string;
  classId: 'platform';
  usedBy: ProfessionalModuleId[];
  kind: ModuleComponentKind;
  summary: string;
  impact: string;
};

/** 业务模块内可单独开关的次级功能。 */
export type BusinessFeatureDefinition = {
  id: string;
  parentId: ProfessionalModuleId;
  name: string;
  impact: string;
};

/** 业务模块下的独立次级功能；关闭不影响父模块及其他功能。 */
export const BUSINESS_FEATURE_DEFINITIONS: BusinessFeatureDefinition[] = [
  {id: 'customs-invoice-matching-v1', parentId: 'finance-management', name: '报关单及发票匹配台账', impact: '关闭后无法创建或进入报关匹配台账任务，不影响发票台账。'},
  {id: 'invoice-ledger-v1', parentId: 'finance-management', name: '发票台账', impact: '关闭后无法创建或进入发票台账任务，不影响报关匹配台账。'},
  {id: 'legal-quick-inspect-v1', parentId: 'legal-compliance-assistant', name: '合同预审', impact: '关闭后无法发起快速预审，不影响智能审核与归档。'},
  {id: 'legal-ai-review-v1', parentId: 'legal-compliance-assistant', name: '智能审核', impact: '关闭后无法发起智能审核与批注导出，不影响预审与归档。'},
  {id: 'legal-archive-v1', parentId: 'legal-compliance-assistant', name: '成果归档', impact: '关闭后无法归档或恢复项目，不影响预审与审核。'},
  {id: 'legal-party-ownership-v1', parentId: 'legal-compliance-assistant', name: '国资与关联识别', impact: '关闭后预审不再查询国资归属与主体关系，不影响其他审核步骤。'},
  {id: 'legal-litigation-ledger-v1', parentId: 'legal-compliance-assistant', name: '诉讼台账', impact: '关闭后无法进入诉讼台账管理，不影响合同预审、审核与归档。'},
];

export const BUSINESS_FEATURE_IDS = BUSINESS_FEATURE_DEFINITIONS.map((item) => item.id);

/** 页面顶层分类。 */
export const MODULE_CLASS_DEFINITIONS: ModuleClassDefinition[] = [
  {
    id: 'business',
    name: '业务功能',
    summary: '客户可见的专业能力。全站总闸在此；开通请到用户或团队空间，价格请到订单管理。',
  },
  {
    id: 'platform',
    name: '平台基础架构',
    summary: '识别、匹配、字段处理与导出等运行时能力，供业务模块调用，不单独对客户售卖。',
  },
];

/** 客户可见的专业模块。开通不在本页完成。 */
export const PROFESSIONAL_MODULE_DEFINITIONS: ProfessionalModuleDefinition[] = [
  {
    id: 'finance-management',
    classId: 'business',
    name: '财务管理模块',
    summary: '报关单识别、发票识别、匹配核对与台账导出。包含报关匹配台账和发票台账两类任务。',
    capabilities: ['报关单及发票匹配台账', '发票台账'],
    entitlementPolicy: 'require_when_listed',
    entitlementPolicyLabel: '计费产品上架后，须持有有效个人授权或企业席位；产品停售期间，系统总闸开启即可使用。',
    serviceLinks: [
      {label: '订单管理', href: '/admin/billing'},
      {label: '用户代开通', href: '/admin/users'},
      {label: '团队席位', href: '/admin/organizations'},
    ],
  },
  {
    id: 'legal-compliance-assistant',
    classId: 'business',
    name: '法务合规助手模块',
    summary: '合同预审、智能审核批注、归档、诉讼台账，以及国资归属与关联合同审核尺度。',
    capabilities: ['合同预审', '智能审核与批注', '归档', '诉讼台账', '国资与关联合同尺度'],
    entitlementPolicy: 'always_require',
    entitlementPolicyLabel: '始终要求有效个人授权或企业席位，即使计费产品暂时停售。',
    serviceLinks: [
      {label: '订单管理', href: '/admin/billing'},
      {label: '用户代开通', href: '/admin/users'},
      {label: '团队席位', href: '/admin/organizations'},
      {label: 'AI 服务', href: '/admin/ai'},
    ],
  },
];

/** 平台基础架构组件。 */
export const MODULE_COMPONENT_DEFINITIONS: ModuleComponentDefinition[] = [
  {id: 'customs-declaration-unified-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '统一报关单识别。', impact: '关闭后无法识别报关单。'},
  {id: 'tax-registration-customs-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '税务登记类报关单识别。', impact: '关闭后税务登记类报关单识别不可用。'},
  {id: 'invoice-unified-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '统一发票识别。', impact: '关闭后无法识别发票。'},
  {id: 'common-field-processing-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '通用字段处理与计算。', impact: '关闭后字段处理与计算不可用。'},
  {id: 'matching-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '发票与报关单匹配核对。', impact: '关闭后无法完成发票匹配核对。'},
  {id: 'ledger-export-v1', classId: 'platform', usedBy: ['finance-management'], kind: 'runtime', summary: '台账生成与导出。', impact: '关闭后无法生成或导出台账。'},
];

/**
 * 某业务模块依赖的基础架构定义。
 * @param moduleId 专业模块
 */
export function componentDefinitionsFor(moduleId: ProfessionalModuleId): ModuleComponentDefinition[] {
  return MODULE_COMPONENT_DEFINITIONS.filter((item) => item.usedBy.includes(moduleId));
}

/**
 * 查找专业模块定义。
 * @param moduleId 模块 ID
 */
export function professionalModuleDefinition(moduleId: string): ProfessionalModuleDefinition | undefined {
  return PROFESSIONAL_MODULE_DEFINITIONS.find((item) => item.id === moduleId);
}

/**
 * 某业务模块下的独立次级功能。
 * @param moduleId 专业模块
 */
export function featureDefinitionsFor(moduleId: ProfessionalModuleId): BusinessFeatureDefinition[] {
  return BUSINESS_FEATURE_DEFINITIONS.filter((item) => item.parentId === moduleId);
}
